import cx from 'classnames';
import { isEmpty } from 'lodash';
import React, { useMemo, Fragment, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';

import {
  Button,
  CopyButton,
  CountdownTimer,
  NspkCustomPaymentForm,
  PaymentDetailsRules,
  PaymentFields,
  PaymentLayout,
} from 'components';
import { EXCLAMATION_CLOUD_ICON_PATH } from 'constants/images.constants';
import { useToast } from 'context';
import { useOrderContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { PaymentDetailsViewProps } from 'types';
import { clipboardUtils } from 'utils';

import { DefaultDeeplinks } from '../DefaultDeeplinks';

import './PaymentDetails.style.scss';

export const DefaultPaymentDetails: React.FC<PaymentDetailsViewProps> = ({
  actions,
}) => {
  const {
    order,
    refetchOrderStatus,
    paymentTypeFlags: { isTSBP, isSIM, isSberPay, isCustomNspkForm },
    customerBankName,
    currencySymbol,
    requisitesCountry,
    deeplinks,
  } = useOrderContext();

  const { showCopiedToast, showCopyFailedToast } = useToast();

  const [changeAmountConfirmed, setChangeAmountConfirmed] = useState(
    order.amount === order.initialAmount,
  );

  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_details',
  });
  const { amount } = useMemo(() => order || {}, [order]);

  const renderTitle = useCallback(
    () => (
      <div className="tw-text-primary tw-text-lg md:tw-text-2xl">
        <span className="tw-font-semibold">
          {t(
            isTSBP ? 'rules_tsbp.title' : isSberPay ? 'open_sberpay' : 'open',
            { bank: customerBankName, country: requisitesCountry },
          )}
        </span>
        {isSIM ? (
          <span className="tw-text-secondary">{t('sim_top_up')}</span>
        ) : (
          !isTSBP && (
            <Fragment>
              <span className="tw-text-secondary">{t('transfer')}</span>
              {!isSberPay && (
                <span className="tw-font-semibold">{t('requisites')}</span>
              )}
            </Fragment>
          )
        )}
        <div
          className={cx('tw-mt-2 tw-flex tw-items-center tw-justify-between', {
            'tw-bg-layout-accent': !changeAmountConfirmed,
          })}
        >
          <span className="tw-font-bold">
            {`${t('amount')}:`}
            <span className="tw-bg-red-100 tw-text-red-800 tw-text-xl tw-font-bold tw-me-2 tw-px-2.5 tw-py-2.5 tw-rounded-sm tw-dark:bg-red-900 tw-dark:text-red-300 tw-mr-3 tw-ml-3 tw-mt-1 tw-mb-1 tw-inline-block">{` ${amount} ${currencySymbol} `}</span>
          </span>
          <CopyButton
            onClick={() =>
              clipboardUtils.copyToClipboardWithFeedback(
                String(amount),
                showCopiedToast,
                showCopyFailedToast,
              )
            }
          />
        </div>
      </div>
    ),
    [
      amount,
      currencySymbol,
      customerBankName,
      isSberPay,
      isTSBP,
      isSIM,
      requisitesCountry,
      changeAmountConfirmed,
      showCopiedToast,
      showCopyFailedToast,
      t,
    ],
  );

  if (isCustomNspkForm) {
    return (
      <NspkCustomPaymentForm
        actions={actions}
        deeplinksSlot={
          !isEmpty(deeplinks) ? (
            <DefaultDeeplinks deeplinks={deeplinks} directOpen />
          ) : null
        }
      />
    );
  }

  return (
    <PaymentLayout actions={changeAmountConfirmed ? actions : []}>
      {!changeAmountConfirmed && (
        <div className="tw-absolute tw-bg-black tw-top-0 tw-left-0 tw-right-0 tw-bottom-0 tw-opacity-70 tw-z-[1]"></div>
      )}
      <div className="tw-w-full">
        <div className="tw-flex tw-items-center tw-w-full tw-mb-4">
          <CountdownTimer
            order={order}
            refetchOrderStatus={refetchOrderStatus}
          />
          <div className="tw-relative tw-z-[3]">
            {renderTitle()}
            {!changeAmountConfirmed && (
              <div className="tw-absolute tw-w-full tw-p-4 tw-mt-4 tw-bg-layout-accent-alt tw-rounded-b-xl">
                <span className="tw-absolute tw-top-[-1.2rem] tw-left-8 tw-text-layout-accent-alt tw-text-2xl tw-scale-x-150">
                  {'▲'}
                </span>
                <div className="tw-text-lg md:tw-text-xl tw-mb-1 tw-font-semibold">
                  {t('change_amount.title')}
                </div>
                <div className="tw-text-lg md:tw-text-xl">
                  {t('change_amount.description')}
                </div>
                <div className="tw-mt-4 tw-w-32">
                  <Button
                    heightClassName="tw-h-10"
                    color="success"
                    onClick={() => setChangeAmountConfirmed(true)}
                  >
                    {t('change_amount.confirm')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        <PaymentFields />
        {!isEmpty(deeplinks) && <DefaultDeeplinks deeplinks={deeplinks} />}
        <div className="tw-flex tw-items-start">
          <div className="tw-hidden tw-items-center tw-justify-center tw-min-w-24 tw-w-24 tw-h-24 tw-mr-8 tw-rounded-2xl tw-bg-layout-accent-alt md:tw-flex">
            <SVG
              src={EXCLAMATION_CLOUD_ICON_PATH}
              title="exclamation-icon"
              className="tw-w-9"
            />
          </div>
          <PaymentDetailsRules requisitesCountry={requisitesCountry} />
        </div>
      </div>
    </PaymentLayout>
  );
};
